import { useCallback, useEffect } from 'react'
import { toast } from 'react-toastify'

import { Select, Textarea } from '@loadsmart/loadsmart-ui'
import type { Selectable } from '@loadsmart/loadsmart-ui/dist/hooks/useSelectable'
import { Dialog, Field, Layout, Text, TextField } from '@loadsmart/miranda-react'

import { formatCurrency } from 'core/utils/currency'

import type { Shipment } from '../domain/Shipment'
import { CHARGE_DESCRIPTION_MAX_CHARS, type ShipmentCharge } from '../domain/ShipmentCharges'
import { useCreateShipmentCharge } from './hooks/useCreateShipmentCharge'
import { useNewChargeForm } from './hooks/useNewChargeForm'
import { useShipmentAccessorialsOptions } from './hooks/useShipmentAccessorialsOptions'
import { useShipmentChargeStopOptions } from './hooks/useShipmentChargeStopOptions'
import { NewChargeAttachmentField } from './NewChargeAttachmentField'
import { TotalField, TotalValueWrapper } from './NewChargeDialog.styles'

type NewChargeDialogProps = {
  isOpen: boolean
  onClose: () => void
  onClosed: () => void
  shipment: Shipment
  onChargeCreated: (createdCharge: ShipmentCharge) => void
  onChargeUpdated: (updatedCharge: ShipmentCharge) => void
}

export function NewChargeDialog({
  isOpen,
  onClose,
  onClosed,
  shipment,
  onChargeCreated,
  onChargeUpdated,
}: NewChargeDialogProps) {
  const { accessorialsOptions } = useShipmentAccessorialsOptions(shipment.mode)

  const {
    state: { accessorial, quantity, unitPrice, totalPrice, stop, description, attachmentFile },
    errors,
    hasErrors,
    onChargeTypeChange,
    onQuantityChange,
    onUnitPriceChange,
    setSelectedStop,
    onStopChange,
    onDescriptionChange,
    onFileInputChange,
    onRemoveAttachment,
  } = useNewChargeForm({ isOpen })

  const { stopOptions } = useShipmentChargeStopOptions({
    shipment,
  })

  useEffect(() => {
    // Reset selected stop when the selected accessorial changes
    if (stopOptions.length === 1 && accessorial) {
      setSelectedStop(stopOptions[0])
    } else {
      setSelectedStop(null)
    }
  }, [stopOptions, accessorial, setSelectedStop])

  const onSuccess = useCallback(
    (charge: ShipmentCharge) => {
      toast.success('Charge created successfully.')

      onClose()
    },
    [onClose]
  )

  const onError = useCallback((error: string) => {
    toast.error(error)
  }, [])

  const { onCreateCharge, isSubmitting, chargeCreated } = useCreateShipmentCharge({
    shipmentUUID: shipment.uuid,
    onSuccess,
    onError,
    onChargeCreated,
    onChargeUpdated,
  })

  function onSubmit() {
    onCreateCharge({
      chargeType: accessorial,
      stop,
      quantity,
      unitPrice,
      description,
      attachmentFile,
    })
  }

  const inputsDisabled = isSubmitting || chargeCreated
  const submitButtonDisabled = (chargeCreated && !attachmentFile) || hasErrors || isSubmitting

  return (
    <Dialog size="medium" open={isOpen} onClose={onClose} onClosed={onClosed}>
      <Dialog.Header>
        Request charge
        <Dialog.Close onClick={onClose} />
      </Dialog.Header>
      <Dialog.Body>
        <Layout.Stack>
          <Field required>
            <Field.Label id="charge-type-select-label">Charge type</Field.Label>
            <Select
              name="charge-type-select"
              aria-labelledby="charge-type-select-label"
              placeholder="Search or select accessorial"
              options={accessorialsOptions}
              value={accessorial as Selectable}
              onChange={onChargeTypeChange}
              hideClear
              disabled={!accessorialsOptions.length || inputsDisabled}
            />
          </Field>

          <Layout.Grid minColumnWidth="130px">
            <Field>
              <Field.Label id="quantity-label">Quantity</Field.Label>
              <TextField
                aria-labelledby="quantity-label"
                placeholder="1"
                type="number"
                min="1"
                value={quantity.toString()}
                onInput={onQuantityChange}
                disabled={inputsDisabled}
              />
            </Field>

            <Field required>
              <Field.Label id="unit-price-label">Amount per unit</Field.Label>
              <TextField
                aria-labelledby="unit-price-label"
                leading="$"
                placeholder="00.00"
                type="number"
                min="1"
                value={unitPrice?.toString()}
                onInput={onUnitPriceChange}
                disabled={inputsDisabled}
              />
            </Field>

            <TotalField>
              <Field.Label id="total-label">Total</Field.Label>
              <TotalValueWrapper align="center">
                <Text variant="heading-md-bold" aria-labelledby="total-label">
                  {formatCurrency(totalPrice, true)}
                </Text>
              </TotalValueWrapper>
            </TotalField>
          </Layout.Grid>

          <Field required>
            <Field.Label id="stop-select-label">Stop</Field.Label>
            <Select
              name="stop-select"
              aria-labelledby="stop-select-label"
              placeholder="Choose the stop where it happened"
              options={stopOptions}
              value={stop as Selectable}
              onChange={onStopChange}
              hideClear
              disabled={inputsDisabled || !accessorial}
            />
          </Field>

          <Field status={errors.description ? 'danger' : 'default'}>
            <Field.Label id="description-label">Description</Field.Label>
            <Textarea
              aria-labelledby="description-label"
              value={description}
              onChange={onDescriptionChange}
              maxLength={CHARGE_DESCRIPTION_MAX_CHARS}
              disabled={inputsDisabled}
            />
            <Field.Hint>Max {CHARGE_DESCRIPTION_MAX_CHARS} chars</Field.Hint>
          </Field>

          <NewChargeAttachmentField
            attachmentFile={attachmentFile}
            errors={errors}
            isSubmitting={isSubmitting}
            onFileInputChange={onFileInputChange}
            onRemoveAttachment={onRemoveAttachment}
          />
        </Layout.Stack>
      </Dialog.Body>
      <Dialog.Actions>
        <Dialog.ActionSecondary onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Dialog.ActionSecondary>
        <Dialog.ActionPrimary
          onClick={onSubmit}
          disabled={submitButtonDisabled}
          loading={isSubmitting}
        >
          Send request to shipper
        </Dialog.ActionPrimary>
      </Dialog.Actions>
    </Dialog>
  )
}
